import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { Text } from '@/components/common/text';
import type { StackScreenProps } from '@react-navigation/stack';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { BaseAdminScreen, AdminErrorBoundary } from '@/components';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import {
  useGetAvailableAssigneesQuery,
  useGetTodoListQuery,
  useSetOccurrenceItemStatusMutation,
} from '@/store/api/admin/todoListsApi';
import type { ItemStatus } from '@/types/admin/todoLists';
import type { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { DesktopTodoListOccurrenceView } from './desktop/DesktopTodoListOccurrenceView';
import { TodoListOccurrenceView } from './mobile/TodoListOccurrenceView';
import type { SavedBannerVariant } from './mobile/TodoListOccurrenceView';

type Props = StackScreenProps<AdminStackParamList, 'TodoListOccurrence'>;

/** How long the post-save confirmation banner stays up before auto-dismissing. */
const SAVED_BANNER_TIMEOUT_MS = 4000;

/**
 * Occurrence detail + item check-off. Responsive host mirroring ToDosScreen:
 * the desktop layout renders the desktop view, everything else renders the
 * mobile view inside BaseAdminScreen.
 *
 * Data: `useGetTodoListQuery(listId)` (list + embedded `currentOccurrence`).
 * Completed-by names are resolved from the already-wired staff list rather than
 * a dedicated endpoint. Item status changes go through the optimistic
 * `setOccurrenceItemStatus` mutation (server is authoritative; the scoped
 * invalidation refetch corrects the header).
 */
export const TodoListOccurrenceScreen: React.FC<Props> = ({ route }) => {
  const { listId } = route.params;
  const savedBannerParam = route.params.savedBanner;
  const { layoutMode } = useResponsive();
  const { redesign } = useAdminColors();
  const { selectedPropertyId } = useAdminUser();
  const navigation = useNavigation<StackNavigationProp<AdminStackParamList>>();

  // Post-save confirmation from the edit flow. The param is one-shot: absorb it
  // into local state, then clear it from the route so a later re-focus /
  // re-navigation can't resurrect a stale banner. It auto-dismisses after a beat.
  // The nonce gives every absorbed param a fresh object identity, so a re-save
  // landing while a banner is still visible restarts the dismiss timer — even
  // when the variant is unchanged.
  const [savedBanner, setSavedBanner] = useState<{
    variant: SavedBannerVariant;
    nonce: number;
  } | null>(savedBannerParam ? { variant: savedBannerParam, nonce: 0 } : null);

  useEffect(() => {
    if (!savedBannerParam) return;
    setSavedBanner((prev) => ({
      variant: savedBannerParam,
      nonce: (prev?.nonce ?? 0) + 1,
    }));
    navigation.setParams({ savedBanner: undefined });
  }, [savedBannerParam, navigation]);

  useEffect(() => {
    if (!savedBanner) return;
    const timer = setTimeout(
      () => setSavedBanner(null),
      SAVED_BANNER_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [savedBanner]);

  const { data: list, isLoading, error } = useGetTodoListQuery(listId);
  const { data: assignees } = useGetAvailableAssigneesQuery(
    { propertyId: selectedPropertyId! },
    { skip: !selectedPropertyId },
  );
  const [setOccurrenceItemStatus] = useSetOccurrenceItemStatusMutation();

  const onSetItemStatus = useCallback(
    (itemId: number, status: ItemStatus) => {
      setOccurrenceItemStatus({ itemId, listId, status });
    },
    [setOccurrenceItemStatus, listId],
  );

  const onEditList = useCallback(() => {
    navigation.navigate('EditTodoList', { listId });
  }, [navigation, listId]);

  // Back affordance: pop the stack when possible, otherwise fall back to the
  // To Do's index route (e.g. when this screen was reached via a deep link
  // and has no prior entry to pop to).
  const onBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('ToDos');
    }
  }, [navigation]);

  const isError = error != null;

  // The loading and no-list shells return early without the desktop/mobile
  // views, so they carry their own back affordance — otherwise a cold deep link
  // to a slow or deleted list strands the user (the navigator hides the header).
  const backLink = (
    <TouchableOpacity
      testID="todo-list-back-button"
      onPress={onBack}
      accessibilityRole="button"
      accessibilityLabel="Back to To Do's"
      style={styles.shellBackLink}
    >
      <Text style={[styles.backLinkText, { color: redesign.text.secondary }]}>
        ‹ To Do's
      </Text>
    </TouchableOpacity>
  );

  if (isLoading && !list) {
    return (
      <AdminErrorBoundary>
        <View style={styles.shell}>
          {backLink}
          <View style={styles.centered}>
            <ActivityIndicator color={redesign.text.secondary} />
          </View>
        </View>
      </AdminErrorBoundary>
    );
  }

  // No list payload and an error → the desktop/mobile view still renders its
  // header-less error state, so synthesize a minimal shell when list is absent.
  const viewProps = {
    list: list!,
    assignees: assignees ?? [],
    isLoading,
    isError: isError || !list,
    onSetItemStatus,
    onBack,
    // Archived lists are read-only server-side, so only expose Edit while active.
    onEditList: list && list.status === 'active' ? onEditList : undefined,
    savedBanner: savedBanner?.variant ?? null,
  };

  if (!list) {
    return (
      <AdminErrorBoundary>
        <BaseAdminScreen
          screenName="TodoListOccurrence"
          showPlaceholder={false}
          scrollEnabled={false}
          contentContainerStyle={styles.baseScreenContent}
        >
          {backLink}
          <View style={styles.centered}>
            <Text style={[styles.errorText, { color: redesign.text.muted }]}>
              This to-do list is unavailable.
            </Text>
          </View>
        </BaseAdminScreen>
      </AdminErrorBoundary>
    );
  }

  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary>
        <DesktopTodoListOccurrenceView {...viewProps} />
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary>
      <BaseAdminScreen
        screenName="TodoListOccurrence"
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <TodoListOccurrenceView {...viewProps} />
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  centered: {
    flex: 1,
    minHeight: 200,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  errorText: { fontSize: 15, textAlign: 'center' },
  shell: { flex: 1 },
  // Mirrors the views' backLink/backLinkText styles; horizontal inset stands in
  // for the shells' missing page padding. Font size stays at the platform
  // default to avoid a new inline typographic literal.
  shellBackLink: {
    alignSelf: 'flex-start',
    paddingVertical: 4,
    paddingHorizontal: 16,
  },
  backLinkText: { fontWeight: '600' },
});
